function ConversationListPanel({
    folderLabel,
    loading,
    error,
    conversations,
    selectedConversationId,
    onSelectConversation,
    messagesLoading,
    messagesError,
    messages,
    searchQuery,
    onSearchQueryChange,
    page,
    totalPages,
    onPrevPage,
    onNextPage,
    sortBy,
    onSortChange,
    isComposeMode,
    onStartCompose,
    composeForm,
    onComposeFormChange,
    onSendCompose,
    onCancelCompose,
    sendingCompose,
    composeFeedback,
    onReplyMessage,
    onReplyAllMessage,
    onForwardMessage,
}) {
    function formatTimestamp(value) {
        if (!value) {
            return "";
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }

        return date.toLocaleString([], {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    }

    function previewText(value, maxLength = 220) {
        const normalized = String(value || "").replace(/\s+/g, " ").trim();
        if (!normalized) {
            return "(no text body)";
        }
        if (normalized.length <= maxLength) {
            return normalized;
        }
        return `${normalized.slice(0, maxLength)}...`;
    }

    function formatAddress(name, email, unknownLabel) {
        const normalizedName = String(name || "").trim();
        const normalizedEmail = String(email || "").trim();
        if (normalizedName && normalizedEmail) {
            return `${normalizedName} <${normalizedEmail}>`;
        }
        if (normalizedEmail) {
            return normalizedEmail;
        }
        if (normalizedName) {
            return normalizedName;
        }
        return unknownLabel;
    }

    return (
        <div className="crm-conv-panel-grid">
            <section className="crm-conv-list-panel">
                <header className="crm-conv-panel-header">
                    <h3>{folderLabel}</h3>
                </header>

                <div className="crm-conv-list-toolbar">
                    <input
                        className="crm-conv-search-input"
                        type="search"
                        placeholder="Search subject or contact"
                        value={searchQuery}
                        onChange={(event) => onSearchQueryChange(event.target.value)}
                    />
                    <div className="crm-conv-sort-row">
                        <label htmlFor="conversationSort">Sort</label>
                        <div className="crm-conv-sort-actions">
                            <select
                                id="conversationSort"
                                className="crm-conv-sort-select"
                                value={sortBy}
                                onChange={(event) => onSortChange(event.target.value)}
                            >
                                <option value="newest">Newest first</option>
                                <option value="oldest">Oldest first</option>
                                <option value="unread-first">Unread first</option>
                            </select>
                            <button
                                type="button"
                                className="crm-conv-compose-trigger"
                                onClick={onStartCompose}
                                aria-label="Compose email"
                                title="Compose email"
                            >
                                +
                            </button>
                        </div>
                    </div>
                    <div className="crm-conv-pagination-controls">
                        <button type="button" onClick={onPrevPage} disabled={page <= 1 || loading}>
                            Prev
                        </button>
                        <span>Page {Math.max(page, 1)} of {Math.max(totalPages, 1)}</span>
                        <button type="button" onClick={onNextPage} disabled={loading || page >= Math.max(totalPages, 1)}>
                            Next
                        </button>
                    </div>
                </div>

                {loading ? <div className="crm-conv-empty">Loading conversations...</div> : null}
                {!loading && error ? <div className="crm-conv-empty">{error}</div> : null}
                {!loading && !error && conversations.length === 0 ? (
                    <div className="crm-conv-empty">No conversations found</div>
                ) : null}

                {!loading && !error && conversations.length > 0 ? (
                    <div className="crm-conv-list">
                        {conversations.map((conversation) => (
                            <button
                                key={String(conversation.id)}
                                type="button"
                                className={`crm-conv-list-item ${selectedConversationId === conversation.id ? "is-active" : ""}`}
                                onClick={() => onSelectConversation(conversation.thread_id)}
                            >
                                <div className="crm-conv-list-subject">{conversation.subject || "No subject"}</div>
                                <div className="crm-conv-list-meta">
                                    <span>
                                        {conversation.account_name || conversation.contact_name || "No account"}
                                        {conversation.account_name && conversation.contact_name
                                            ? ` · ${conversation.contact_name}`
                                            : ""}
                                    </span>
                                    <span>{conversation.message_count || 0} msgs · {formatTimestamp(conversation.updated_at)}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                ) : null}
            </section>

            <section className="crm-conv-messages-panel">
                <header className="crm-conv-panel-header">
                    <h3>{isComposeMode ? "Compose" : "Messages"}</h3>
                </header>

                {isComposeMode ? (
                    <div className="crm-conv-compose-panel">
                        {composeFeedback ? <p className="crm-conv-compose-note">{composeFeedback}</p> : null}
                        <div className="crm-conv-compose-fields">
                            <input
                                type="email"
                                placeholder="To"
                                value={composeForm.to}
                                onChange={(event) => onComposeFormChange("to", event.target.value)}
                                disabled={sendingCompose}
                            />
                            <input
                                type="text"
                                placeholder="Subject"
                                value={composeForm.subject}
                                onChange={(event) => onComposeFormChange("subject", event.target.value)}
                                disabled={sendingCompose}
                            />
                            <textarea
                                rows={12}
                                placeholder="Message"
                                value={composeForm.body}
                                onChange={(event) => onComposeFormChange("body", event.target.value)}
                                disabled={sendingCompose}
                            />
                        </div>
                        <div className="crm-conv-compose-actions">
                            <button
                                type="button"
                                className="crm-conv-compose-send-button"
                                onClick={onSendCompose}
                                disabled={sendingCompose}
                            >
                                {sendingCompose ? "Sending..." : "Send Email"}
                            </button>
                            <button
                                type="button"
                                className="crm-conv-compose-cancel"
                                onClick={onCancelCompose}
                                disabled={sendingCompose}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : null}

                {!isComposeMode && !selectedConversationId ? (
                    <div className="crm-conv-empty">Select a conversation to view messages</div>
                ) : null}
                {!isComposeMode && selectedConversationId && messagesLoading ? (
                    <div className="crm-conv-empty">Loading messages...</div>
                ) : null}
                {!isComposeMode && selectedConversationId && !messagesLoading && messagesError ? (
                    <div className="crm-conv-empty">{messagesError}</div>
                ) : null}
                {!isComposeMode && selectedConversationId && !messagesLoading && !messagesError && messages.length === 0 ? (
                    <div className="crm-conv-empty">No messages in this conversation</div>
                ) : null}

                {!isComposeMode && selectedConversationId && !messagesLoading && !messagesError && messages.length > 0 ? (
                    <div className="crm-conv-messages-list">
                        {messages.map((message) => (
                            <article key={String(message.id)} className="crm-conv-message-item">
                                <div className="crm-conv-message-meta">
                                    <span>{message.direction || "message"}</span>
                                    <span>{formatTimestamp(message.sent_at)}</span>
                                </div>
                                <div className="crm-conv-message-addresses">
                                    <div className="crm-conv-message-address-row">
                                        <span className="crm-conv-message-address-label">From:</span>
                                        <span className="crm-conv-message-address-value">
                                            {formatAddress(message.from_name, message.from_email, "Unknown sender")}
                                        </span>
                                    </div>
                                    <div className="crm-conv-message-address-row">
                                        <span className="crm-conv-message-address-label">To:</span>
                                        <span className="crm-conv-message-address-value">
                                            {formatAddress(message.to_name, message.to_email, "Unknown recipient")}
                                        </span>
                                    </div>
                                </div>
                                <div className="crm-conv-message-subject">{message.subject || "No subject"}</div>
                                <p className="crm-conv-message-body">{previewText(message.body_text)}</p>
                                <div className="crm-conv-message-actions">
                                    <button
                                        type="button"
                                        className="crm-conv-message-action"
                                        onClick={() => onReplyMessage?.(message)}
                                    >
                                        Reply
                                    </button>
                                    <button
                                        type="button"
                                        className="crm-conv-message-action"
                                        onClick={() => onReplyAllMessage?.(message)}
                                    >
                                        Reply All
                                    </button>
                                    <button
                                        type="button"
                                        className="crm-conv-message-action"
                                        onClick={() => onForwardMessage?.(message)}
                                    >
                                        Forward
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                ) : null}
            </section>
        </div>
    );
}

export default ConversationListPanel;
